import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Target } from 'lucide-react';
import { useGetList } from 'ra-core';
import { Link } from 'react-router-dom';

const STAGES = [
    { id: 'inquiry', label: 'Inquiry', color: 'bg-slate-400' },
    { id: 'requirement_discussion', label: 'Req. Discussion', color: 'bg-info' },
    { id: 'proposal', label: 'Proposal', color: 'bg-purple-500' },
    { id: 'won', label: 'Won', color: 'bg-success' },
    { id: 'lost', label: 'Lost', color: 'bg-destructive' },
] as const;

const formatLakh = (v: number) => `₹${(v / 100000).toFixed(1)}L`;

/**
 * SalesPipelineWidget
 *
 * Dashboard card summarizing open opportunities by stage — count and pipeline value.
 */
export const SalesPipelineWidget = () => {
    const { data: opportunities, isPending } = useGetList('opportunities', {
        pagination: { page: 1, perPage: 1000 },
        filter: {},
    });

    const openValue = (opportunities || [])
        .filter((o) => o.stage !== 'won' && o.stage !== 'lost')
        .reduce((sum, o) => sum + (o.deal_value || 0), 0);

    return (
        <Card className="border-none shadow-sm">
            <CardHeader className="pb-3 border-b">
                <CardTitle className="text-xs font-bold uppercase tracking-widest text-muted-foreground flex items-center gap-2">
                    <Target className="w-3.5 h-3.5" /> Sales Pipeline
                    <span className="ml-auto text-foreground font-black text-xs normal-case tracking-normal">
                        {formatLakh(openValue)} open
                    </span>
                </CardTitle>
            </CardHeader>
            <CardContent className="pt-3">
                {isPending ? (
                    <div className="py-6 text-center text-muted-foreground text-xs animate-pulse">Loading pipeline…</div>
                ) : !opportunities?.length ? (
                    <div className="py-6 text-center text-muted-foreground flex flex-col items-center gap-2">
                        <Target className="w-8 h-8 opacity-40" />
                        <p className="text-xs">No opportunities yet.</p>
                    </div>
                ) : (
                    <div className="space-y-2">
                        {STAGES.map((stage) => {
                            const opps = opportunities.filter((o) => o.stage === stage.id);
                            const value = opps.reduce((sum, o) => sum + (o.deal_value || 0), 0);
                            const maxCount = Math.max(...STAGES.map((s) => opportunities.filter((o) => o.stage === s.id).length), 1);
                            return (
                                <div key={stage.id} className="flex items-center gap-2 text-xs">
                                    <span className="w-28 shrink-0 text-muted-foreground truncate">{stage.label}</span>
                                    <div className="flex-1 h-2 rounded-full bg-muted overflow-hidden">
                                        <div
                                            className={`h-full rounded-full ${stage.color}`}
                                            style={{ width: `${(opps.length / maxCount) * 100}%` }}
                                        />
                                    </div>
                                    <span className="w-6 text-right font-semibold text-foreground shrink-0">{opps.length}</span>
                                    <span className="w-16 text-right text-muted-foreground shrink-0">{formatLakh(value)}</span>
                                </div>
                            );
                        })}
                    </div>
                )}
                <div className="pt-3 mt-3 border-t text-center">
                    <Link to="/opportunities" className="text-[11px] text-blue-600 hover:underline font-semibold">
                        View full pipeline →
                    </Link>
                </div>
            </CardContent>
        </Card>
    );
};
